import { Grid } from '@mui/material';
import { useContext, useState } from 'react';
import 'moment/locale/en-gb';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import ExportDataButton, {
  ExportRequestParameters,
  ExportParametersProps
} from '../ExportDataButton';
import { momentISOString } from '../../../util/date';

// Data sent in request to unique Cloud Function.
interface MetricRecordActivityExportRequestParameters extends ExportRequestParameters {
  group_id: string;
  company_id: string;
  start_date: string;
  end_date: string;
}

/**
 * Export parameters for metric record activity audit log data.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const MetricRecordActivityExportParameters = ({ export_type }: ExportParametersProps) => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) {
    return null;
  }

  const initial_parameters: MetricRecordActivityExportRequestParameters = {
    group_id: group.id,
    company_id: company.id,
    start_date: '',
    end_date: ''
  };

  const [metricRecordActivityExportParameters, setMetricRecordActivityExportParameters] =
    useState<MetricRecordActivityExportRequestParameters>(initial_parameters);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  // Valid when both dates are set, range does not exceed 1 year, and no future dates.
  const parameters_valid = Boolean(
    startDate &&
    endDate &&
    startDate >= moment().subtract(2, 'years') &&
    endDate >= startDate &&
    endDate.diff(startDate, 'years', true) <= 1 &&
    endDate <= moment()
  );

  const handleStartDateChange = (start_date: Moment | null): void => {
    const updated_parameters: MetricRecordActivityExportRequestParameters = {
      ...metricRecordActivityExportParameters,
      start_date: start_date ? momentISOString(start_date) : ''
    };
    setStartDate(start_date);
    setMetricRecordActivityExportParameters(updated_parameters);
  };

  const handleEndDateChange = (end_date: Moment | null): void => {
    // Add 24 hours to include the full final day.
    const end_date_inclusive: Moment | null = end_date ? moment(end_date).add(1, 'day') : null;

    const updated_parameters: MetricRecordActivityExportRequestParameters = {
      ...metricRecordActivityExportParameters,
      end_date: end_date_inclusive ? momentISOString(end_date_inclusive) : ''
    };
    setEndDate(end_date);
    setMetricRecordActivityExportParameters(updated_parameters);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Grid container spacing={2}>
        <Grid size={'auto'} sx={{ minWidth: 250 }}>
          <DatePicker<Moment>
            label={'Start Date'}
            value={startDate}
            disableFuture
            maxDate={endDate ? moment.min(endDate, moment()) : moment()}
            minDate={
              endDate
                ? moment.max(endDate.clone().subtract(1, 'year'), moment().subtract(2, 'years'))
                : moment().subtract(2, 'years')
            }
            onChange={(new_date) => handleStartDateChange(new_date)}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid size={'auto'} sx={{ minWidth: 250 }}>
          <DatePicker<Moment>
            label={'End Date'}
            value={endDate}
            disableFuture
            maxDate={startDate ? moment.min(startDate.clone().add(1, 'year'), moment()) : moment()}
            minDate={startDate ? startDate : moment().subtract(2, 'years')}
            onChange={(new_date) => handleEndDateChange(new_date)}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid size={'auto'} sx={{ minWidth: 250 }}>
          <ExportDataButton
            user={user}
            export_type={export_type}
            parameters={metricRecordActivityExportParameters}
            disabled={!parameters_valid}
            label="Export"
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default MetricRecordActivityExportParameters;
